import React, { useMemo } from 'react';
import moment from 'moment';
import { Modal, Spin, Table } from 'antd';
import { Tag, Tooltip } from 'components/ui';
import UnblockGmailAccount from './UnblockGmailAccount';
import NameAvatar from './NameAvatar';
import { useFetchGmailAccountMetrics } from 'query/gmail';

const columns = [
  {
    title: '',
    dataIndex: 'key',
    width: 200,
    align: 'center',
    render: v => <a>{v}</a>,
  },
  {
    title: '',
    dataIndex: 'value',
    align: 'center',
  },
];

const fineBoolean = (value, trueAsSuccess) => {
  const showStyle = trueAsSuccess === true || trueAsSuccess === false;
  if (value === 0 || value === null || value === undefined)
    return (
      <div
        style={
          showStyle ? { color: trueAsSuccess === true ? 'Red' : 'Green' } : {}
        }
      >
        NO
      </div>
    );
  return (
    <div
      style={
        showStyle ? { color: trueAsSuccess === true ? 'Green' : 'Red' } : {}
      }
    >
      YES
    </div>
  );
};

const fineValue = value => {
  if (!value) return 'N/A';
  return value;
};

const LoadableFineValue = ({ isLoading, value }) => {
  return <Spin spinning={isLoading}>{fineValue(value)}</Spin>;
};

export default function GmailAccountDetails({ id, data, onClose }) {
  const { data: accountMetrics, isLoading } = useFetchGmailAccountMetrics(id);
  const tableData = [
    { key: 'Name', value: data.display_name },
    { key: 'Email', value: data.email },
    { key: 'ID', value: data.id },
    { key: 'Authorized On App', value: data.authorized_app?.toUpperCase() },
    {
      key: 'Pending',
      value: data.num_pending ? data.num_pending : 'None',
    },
    {
      key: 'Active',
      value: fineBoolean(
        !(data.suspended_at || data.revoked || data.as_reply_to) ? 1 : 0,
        true,
      ),
    },
    { key: 'Revoked', value: fineBoolean(data.revoked, false) },
    { key: 'Suspended', value: fineBoolean(data.suspended_at, false) },
    { key: 'Reply only', value: fineBoolean(data.as_reply_to) },
    { key: 'Date Added', value: fineValue(data.created_at) },
    { key: 'Last revoked', value: fineValue(data.last_revoked_on) },
    { key: 'Last authorized', value: fineValue(data.last_authorized_on) },
    { key: 'Last sent template', value: fineValue(data.last_template_id) },
    {
      key: 'Total Sent (24 hours)',
      value: (
        <LoadableFineValue
          isLoading={isLoading}
          value={accountMetrics.total_sent_last_24_hours}
        />
      ),
    },
    {
      key: 'Total Sent (7 days)',
      value: (
        <LoadableFineValue
          isLoading={isLoading}
          value={accountMetrics.total_sent_last_7_days}
        />
      ),
    },
    {
      key: 'Total Sent (30 days)',
      value: (
        <LoadableFineValue
          isLoading={isLoading}
          value={accountMetrics.total_sent_last_30_days}
        />
      ),
    },
    { key: 'Total Sent (Today)', value: fineValue(data.today_sent_count) },
    { key: 'Total Sent (Lifetime)', value: fineValue(data.total_sent_count) },
  ];

  if (data.suspended_at) {
    tableData.push({
      key: 'Last suspended',
      value: fineValue(data.last_suspended),
    });
  }

  const renderBadge = useMemo(() => {
    if (data.revoked) return <Tag color="red">Revoked</Tag>;
    if (!data.suspended_at) {
      return (
        <span
          className="material-icons"
          style={{ color: 'green', fontSize: 'inherit' }}
        >
          {data.as_reply_to ? 'mailbox' : 'verified'}
        </span>
      );
    }
    const dateRender = moment(data.suspended_at).format('YYYY/MM/DD HH:mm:ss');
    return (
      <Tooltip title={`Suspended at ${dateRender}`}>
        <Tag color="red">Suspended</Tag>
      </Tooltip>
    );
  }, [data.suspended_at, data.revoked, data.as_reply_to]);

  return (
    <Modal
      footer={null}
      title={
        <div
          style={{
            display: 'flex',
          }}
        >
          <div>
            <NameAvatar display_name={data.display_name} />
          </div>
          <div style={{ marginLeft: 15, fontSize: 13 }}>
            <div
              style={{ color: 'grey', display: 'flex', alignItems: 'center' }}
            >
              {data.display_name}
              <div style={{ marginLeft: 5 }}>{renderBadge}</div>
            </div>
            <div style={{ fontWeight: 'bold', fontSize: 15 }}>{data.email}</div>
          </div>
        </div>
      }
      visible
      onCancel={onClose}
    >
      <UnblockGmailAccount data={data} id={id} />

      <Table
        bordered
        columns={columns}
        dataSource={tableData}
        pagination={false}
        showHeader={false}
        size="small"
      />
    </Modal>
  );
}
